import { useEffect, useSyncExternalStore } from 'react'
import type {
  Administratie, AiStatus, BeurtStand, GesprekMeta, Ik, IkAntwoord, Mens, Toegang,
} from '../../gedeeld/types'
import { c, LEEG } from './api'

/* ===========================================================================
 *  Wat de hele app deelt
 *
 *  Een kleine winkel zonder bibliotheek: een object, een lijst luisteraars,
 *  en useSyncExternalStore. Wat erin staat is wat bijna elk scherm nodig
 *  heeft -- wie je bent, waar je bij kunt, hoe het met de pc op kantoor
 *  staat, en de lijst gesprekken in de zijbalk -- en wat dus niet op elk
 *  scherm opnieuw opgehaald hoort te worden.
 *
 *  De berichten van een gesprek staan er NIET in. Die horen bij het scherm
 *  van dat gesprek en worden daar opgehaald; een winkel met alle berichten
 *  van alle gesprekken is een winkel die altijd een beetje oud is.
 * =========================================================================== */

interface Staat {
  ik: Ik | null
  /** Het antwoord op de actie ik; null zolang dat nog niet binnen is. */
  toegang: Toegang[]
  administraties: Administratie[]
  /** Cowork-rechten; null zolang onbekend (dan niets verbergen: de server beslist). */
  rechten: string[] | null
  voorbeelden: string[]
  ai: AiStatus | null
  ikGeladen: boolean

  gesprekken: GesprekMeta[]
  gesprekkenGeladen: boolean

  mensen: Mens[]

  /**
   * Een vraag die elders begon (een terugkerende taak die je met "Nu
   * uitvoeren" aanzette) en die het gespreksscherm moet gaan volgen zodra
   * het opent.
   */
  teVolgen: BeurtStand | null

  meldingen: { id: number; soort: 'ok' | 'fout' | 'info'; tekst: string }[]

  /**
   * Hoeveel bestanden het open gesprek heeft (null: er is geen gesprek
   * open), voor de paperclip in de kop; en of het paneel openstaat.
   */
  bestandenTeller: number | null
  bestandenPaneel: boolean

  /** De zijbalk: op een breed scherm in te klappen, op een smal een lade. */
  zijbalkDicht: boolean
  ladeOpen: boolean
}

const ZIJBALK_SLEUTEL = 'cowork.zijbalk'

/* Alleen een voorkeur van de weergave staat in de opslag van de browser;
   gegevens nooit. In een privévenster gooit localStorage, en dan geldt de
   keuze alleen voor deze keer. */
function leesZijbalk(): boolean {
  try { return localStorage.getItem(ZIJBALK_SLEUTEL) === 'dicht' } catch { return false }
}

let staat: Staat = {
  ik: null,
  toegang: [],
  administraties: [],
  rechten: null,
  voorbeelden: [],
  ai: null,
  ikGeladen: false,
  gesprekken: [],
  gesprekkenGeladen: false,
  mensen: [],
  teVolgen: null,
  meldingen: [],
  bestandenTeller: null,
  bestandenPaneel: false,
  zijbalkDicht: leesZijbalk(),
  ladeOpen: false,
}

const luisteraars = new Set<() => void>()

export function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function lees(): Staat { return staat }

function abonneer(l: () => void) {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore(abonneer, () => kies(staat))
}

/* ------------------------------------------------------------------ *
 *  Meldingen in beeld
 * ------------------------------------------------------------------ */

let volgnummer = 0

export function meld(tekst: string, soort: 'ok' | 'fout' | 'info' = 'ok') {
  const id = ++volgnummer
  zet({ meldingen: [...staat.meldingen, { id, soort, tekst }].slice(-4) })
  /* Een fout blijft langer staan: die moet je kunnen lezen. */
  setTimeout(() => zet({ meldingen: staat.meldingen.filter((m) => m.id !== id) }), soort === 'fout' ? 8000 : 3200)
}

export const meldFout = (e: unknown) => meld(e instanceof Error ? e.message : String(e), 'fout')

/* ------------------------------------------------------------------ *
 *  Wie je bent en waar je bij kunt
 * ------------------------------------------------------------------ */

export function zetIk(a: IkAntwoord) {
  zet({
    ik: a.ik,
    toegang: a.toegang,
    administraties: a.administraties,
    rechten: Array.isArray(a.rechten) ? a.rechten : null,
    voorbeelden: a.voorbeelden,
    ai: a.ai,
    ikGeladen: true,
  })
}

/** De pc op kantoor: staat hij aan? Elke minuut opnieuw gevraagd vanuit de zijbalk. */
export async function verversAi(): Promise<void> {
  try {
    const { ai } = await c('ai-online', LEEG)
    zet({ ai })
  } catch {
    /* Stil: een gemiste keer is geen nieuws, de volgende minuut komt vanzelf. */
  }
}

/* ------------------------------------------------------------------ *
 *  De gesprekken in de zijbalk
 * ------------------------------------------------------------------ */

let gesprekkenBezig: Promise<void> | null = null

export function laadGesprekken(): Promise<void> {
  /* Twee schermen die tegelijk verversen vragen het één keer. */
  if (gesprekkenBezig) return gesprekkenBezig
  gesprekkenBezig = c('gesprekken', {})
    .then(({ gesprekken }) => zet({ gesprekken, gesprekkenGeladen: true }))
    .catch((e) => { zet({ gesprekkenGeladen: true }); meldFout(e) })
    .finally(() => { gesprekkenBezig = null })
  return gesprekkenBezig
}

/** Een gesprek dat ergens veranderde (nieuw, hernoemd, vastgezet): bovenaan of op zijn plek. */
export function gesprekBijwerken(g: GesprekMeta) {
  const er = staat.gesprekken.some((x) => x.id === g.id)
  zet({
    gesprekken: er
      ? staat.gesprekken.map((x) => (x.id === g.id ? { ...x, ...g } : x))
      : [g, ...staat.gesprekken],
  })
}

export function gesprekWeg(id: string) {
  zet({ gesprekken: staat.gesprekken.filter((x) => x.id !== id) })
}

/* ------------------------------------------------------------------ *
 *  Collega's, voor het toewijzen van een taak uit een voorstel
 * ------------------------------------------------------------------ */

let mensenBezig: Promise<void> | null = null

export function laadMensen(): Promise<void> {
  if (mensenBezig) return mensenBezig
  mensenBezig = c('mensen', LEEG)
    .then(({ mensen }) => zet({ mensen }))
    .catch((e) => { mensenBezig = null; meldFout(e) })
  return mensenBezig
}

/** De collega's, opgehaald bij het eerste scherm dat ze vraagt. */
/** Mag deze persoon dit in Cowork? Zolang het onbekend is: ja; de server controleert het toch. */
export function useMag(recht: string): boolean {
  return useStaat((s) => s.rechten === null || s.rechten.includes(recht))
}

export function useMensen(nodig = true): Mens[] {
  const mensen = useStaat((s) => s.mensen)
  useEffect(() => { if (nodig) void laadMensen() }, [nodig])
  return mensen
}

/* ------------------------------------------------------------------ *
 *  De zijbalk
 * ------------------------------------------------------------------ */

export function zetZijbalkDicht(dicht: boolean) {
  try {
    if (dicht) localStorage.setItem(ZIJBALK_SLEUTEL, 'dicht')
    else localStorage.removeItem(ZIJBALK_SLEUTEL)
  } catch { /* zie leesZijbalk */ }
  zet({ zijbalkDicht: dicht })
}

export const zetLade = (open: boolean) => zet({ ladeOpen: open })
